import {
  DEFAULT_BOX_STYLE,
  DEFAULT_THEME_CONTACT_EXCHANGE_FLOW,
  DEFAULT_GLOBAL_DATA_POSITION,
  THEME_CONFIG_KEYS,
} from '@lemontaps/lib/constants';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchPublicThemeConfig, fetchThemeConfig } from '../apis/md/customization/jsonConfigs';
import { AccountImageState, ProfileDesignConfig } from '@lemontaps/lib/types/api';
import { RequireOnlyOne } from '@lemontaps/lib/types/util';
import { useAppSelector } from '@/application/hooks';
import {
  defaultGlobalBanner,
  defaultGlobalProfileAvatar,
} from '@lemontaps/lib/business-logic/features/profile/getter';

export const initialValues = {
  boxStyle: DEFAULT_BOX_STYLE,
  leadGenColor: '',
  fileBoxColor: '',
  genericWebsiteColor: '',
  privacyUrl: '',
  imprintUrl: '',
  showCopyright: false,
  legalName: '',
  leadGenFormShown: false,
  contactExchangeFlow: DEFAULT_THEME_CONTACT_EXCHANGE_FLOW,
  bannerImgOptions: [],
  logoImgOptions: [],
  themeLinksPosition: DEFAULT_GLOBAL_DATA_POSITION,
  themeFilesPosition: DEFAULT_GLOBAL_DATA_POSITION,
  defaultBannerImageUrl: defaultGlobalBanner,
  defaultBannerImageState: AccountImageState.DEFINED,
  defaultProfileImageUrl: defaultGlobalProfileAvatar,
  defaultProfileImageState: AccountImageState.DEFINED,
  logoHeaderUrl: null,
  logoHeaderState: AccountImageState.DELETED,
  autoVCardDownload: false,
  showTeamDirectoryBox: false,
  mainContentBoxOrder: [],
  nameSuffixConnector: '',
  font: null,
  showLeftBorder: true,
  customCss: '',
  customFavicon: null,
};

const ProfileDesignContext = React.createContext<{
  profileDesign: ProfileDesignConfig;
  profileDesignLoaded: boolean;
  refetch(): void;
}>({
  profileDesign: { ...initialValues },
  profileDesignLoaded: false,
  refetch: () => {},
});

export const ProfileDesignProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const domainVerified = useAppSelector(state => state.login.domainVerified);
  const { getAccessTokenSilently } = useAuth0();
  const [profileDesign, setProfileDesign] = React.useState<ProfileDesignConfig>({
    ...initialValues,
  });
  const [profileDesignLoaded, setProfileDesignLoaded] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const { value: profileDesign } = await fetchThemeConfig<ProfileDesignConfig>(
        getAccessTokenSilently,
        THEME_CONFIG_KEYS.PROFILE_DESIGN,
      );
      setProfileDesign(profileDesign);
      setProfileDesignLoaded(true);
    } catch {
      setProfileDesignLoaded(true);
    }
  }, [getAccessTokenSilently]);

  React.useEffect(() => {
    if (domainVerified) fetchData();
  }, [fetchData, domainVerified]);

  return (
    <ProfileDesignContext.Provider
      value={{
        profileDesign: profileDesign,
        refetch: fetchData,
        profileDesignLoaded,
      }}
    >
      {children}
    </ProfileDesignContext.Provider>
  );
};

export const useProfileDesign = () => {
  const context = React.useContext(ProfileDesignContext);
  if (context === undefined) {
    throw new Error('useProfileDesign must be used within a ');
  }
  return context;
};

const useExternalProfileDesign = (
  usernameOrAccountId: RequireOnlyOne<
    { username: string | undefined; accountId: string | undefined },
    'accountId' | 'username'
  >,
) => {
  const [profileDesign, setProfileDesign] = useState<ProfileDesignConfig | null>(null);

  const fetchProfileDesign = useCallback(
    () =>
      fetchPublicThemeConfig(THEME_CONFIG_KEYS.PROFILE_DESIGN, usernameOrAccountId)
        .then(setProfileDesign)
        .catch(() => setProfileDesign(initialValues)),
    [usernameOrAccountId],
  );

  useEffect(() => {
    if (usernameOrAccountId.username || usernameOrAccountId.accountId) {
      fetchProfileDesign();
    }
  }, [fetchProfileDesign, usernameOrAccountId.accountId, usernameOrAccountId.username]);

  return { profileDesign, refetch: fetchProfileDesign };
};
export const useExternalProfileDesignByUsername = (username?: string) => {
  const obj = useMemo(() => ({ username }), [username]);
  const returnObjs = useExternalProfileDesign(obj);
  return returnObjs;
};
